import React, { useState } from 'react'
import Button from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import {
    DEFAULT_TOKEN_EXPIRY_SECONDS,
    NewServiceApp,
    SERVICE_APP_CAPABILITIES,
    getTokenExpiryOptions,
} from '../../schema'
import './createServiceAppModal.scss'
import { useTranslation } from 'translations'

interface CreateServiceAppModalProps {
    accountSlug: string
    saving: boolean
    onCancel: () => void
    onCreate: (app: NewServiceApp) => void
}

export const CreateServiceAppModal: React.FC<CreateServiceAppModalProps> = ({
    accountSlug,
    saving,
    onCancel,
    onCreate,
}) => {
    const { t } = useTranslation()
    const [label, setLabel] = useState('')
    const [expiry, setExpiry] = useState(String(DEFAULT_TOKEN_EXPIRY_SECONDS))

    const canCreate = !!label.trim() && !saving

    const submit = () =>
        onCreate({
            label: label.trim(),
            accounts: [accountSlug],
            permissions: { [accountSlug]: SERVICE_APP_CAPABILITIES },
            tokenExpirySeconds: Number(expiry),
        })

    return (
        <Modal
            title={t('settings.personalSettings.serviceApps.createModal.title')}
            onDismiss={saving ? undefined : onCancel}
            className="create-service-app-modal"
        >
            <ModalBody>
                <p className="create-service-app-intro">
                    {t('settings.personalSettings.serviceApps.createModal.intro')}
                </p>

                <div className="create-service-app-field">
                    <label className="create-service-app-label" htmlFor="service-app-label">
                        {t('settings.personalSettings.serviceApps.createModal.nameLabel')}
                    </label>
                    <input
                        id="service-app-label"
                        className="create-service-app-input"
                        type="text"
                        value={label}
                        maxLength={200}
                        placeholder={t('settings.personalSettings.serviceApps.createModal.namePlaceholder')}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                    <span className="create-service-app-hint">
                        {t('settings.personalSettings.serviceApps.createModal.nameHint')}
                    </span>
                </div>

                <div className="create-service-app-field">
                    <span className="create-service-app-label">
                        {t('settings.personalSettings.serviceApps.createModal.capabilitiesLabel')}
                    </span>
                    <div className="create-service-app-capabilities">
                        {SERVICE_APP_CAPABILITIES.map((capability) => (
                            <span className="create-service-app-capability" key={capability}>
                                {capability}
                            </span>
                        ))}
                    </div>
                    <span className="create-service-app-hint">
                        {t('settings.personalSettings.serviceApps.createModal.capabilitiesHint')}
                    </span>
                </div>

                <div className="create-service-app-field">
                    <label className="create-service-app-label" htmlFor="service-app-expiry">
                        {t('settings.personalSettings.serviceApps.createModal.expiryLabel')}
                    </label>
                    <select
                        id="service-app-expiry"
                        className="create-service-app-input"
                        value={expiry}
                        onChange={(e) => setExpiry(e.target.value)}
                    >
                        {getTokenExpiryOptions().map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <span className="create-service-app-hint">
                        {t('settings.personalSettings.serviceApps.createModal.expiryHint')}
                    </span>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button kind="default" secondary onClick={onCancel} disabled={saving}>
                    {t('common.cancel')}
                </Button>
                <DisabledItemHoverInfotip
                    active={!canCreate}
                    infotipText={
                        saving
                            ? t('settings.personalSettings.serviceApps.createModal.creatingInfotip')
                            : t('settings.personalSettings.serviceApps.createModal.nameRequiredInfotip')
                    }
                    width="18rem"
                    toTheLeft
                >
                    <Button kind="action" disabled={!canCreate} onClick={submit}>
                        {saving ? t('settings.personalSettings.serviceApps.createModal.creating') : t('common.create')}
                    </Button>
                </DisabledItemHoverInfotip>
            </ModalFooter>
        </Modal>
    )
}
